/* eslint-disable react/prop-types */

import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  createStackNavigator,
  CardStyleInterpolators,
} from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb, isNative } from '@global/screens';
import Toast from 'react-native-toast-message';

import {
  setTitleHeader,
  setDefaultStackOption,
  hasNoAccess,
} from '@global/helpers';

import HamburgerButton from '@components/navbar/HamburgerButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import BackButton from '@components/navbar/BackButton';
import MoreButton from '@components/navbar/MoreButton';
import CommentHeaderButton from '@components/comment/CommentHeaderButton';

import ReturnOrderScreen from '@/screens/transaction/return-order/ReturnOrderScreen';
import ReturnOrderDetailScreen from '@/screens/transaction/return-order/ReturnOrderDetailScreen';
import ReturnOrderInvoiceScreen from '@/screens/transaction/return-order/ReturnOrderInvoiceScreen';
import ReturnOrderDocumentScreen from '@/screens/transaction/return-order/ReturnOrderDocumentScreen';
import ReturnOrderFormScreen from '@/screens/transaction/return-order/RerturnOrderFormScreen';

import { MsOrder } from '@api/ms-order';
import { showAlert } from '@/store/global';
import { showPopup } from '@/store/modal';

const Stack = createStackNavigator();

const ReturnOrderNavigator = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const [refresh, setRefresh] = useState(false);
  const tempData = useSelector((state) => state.global?.tempData?.status);
  const current_approval = useSelector(
    (state) => state.global?.tempData?.current_approval,
  );
  const userRole = useSelector((state) => state.auth?.user?.role?.code);

  return (
    <Stack.Navigator
      initialRouteName="ReturnOrderScreen"
      screenOptions={(props) => ({
        headerLeft: () => <HamburgerButton {...props} />,
        headerRight: () => <NotificationSettingButton {...props} />,
        ...setDefaultStackOption(insets, CardStyleInterpolators, true),
      })}>
      <Stack.Screen
        name="ReturnOrderScreen"
        component={ReturnOrderScreen}
        options={(props) => {
          let isBack = false;
          let title = 'Return Order';
          if (props?.route?.params?.titleScreen) {
            isBack = true;
            title = `${title} ${
              !props?.route?.params?.isHome
                ? props.route.params.titleScreen
                : ''
            }`;
          }
          const showFilter = () => {
            dispatch(showPopup({ name: 'showFilter', value: 'return-order' }));
          };
          const showExport = () => {
            dispatch(
              showPopup({ name: 'showReturnOrdersExport', value: true }),
            );
          };
          const menus = [
            {
              type: 'function',
              name: 'Export',
              callback: showExport,
            },
            { type: 'function', name: 'Filter', callback: showFilter },
          ];
          const goBack = () => {
            if (props?.route?.params?.isHome) {
              props.navigation.replace('ReturnOrderScreen', {
                customerId: '',
                titleScreen: '',
              });
              props.navigation.navigate('HomeNavigator');
            } else if (
              props?.route?.params?.customerId &&
              props?.route?.params?.titleScreen
            ) {
              const customerId = JSON.parse(
                JSON.stringify(props.route.params.customerId),
              );
              const titleScreen = JSON.parse(
                JSON.stringify(props.route.params.titleScreen),
              );
              props.navigation.replace('ReturnOrderScreen', {
                customerId: '',
                titleScreen: '',
              });
              props.navigation.navigate('CustomerNavigator', {
                screen: 'CustomerDetailScreen',
                params: {
                  id: customerId,
                  name: titleScreen,
                },
              });
            }
          };
          return {
            title: setTitleHeader(title),
            headerLeft: () =>
              isBack ? (
                <BackButton {...props} onPress={() => goBack()} />
              ) : (
                <HamburgerButton {...props} />
              ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : (
                <MoreButton menu={menus} {...props} />
              ),
          };
        }}
      />
      <Stack.Screen
        name="ReturnOrderDetailScreen"
        component={ReturnOrderDetailScreen}
        options={(props) => {
          const hasAccess = hasNoAccess([
            'vice_president_finance',
            'budget_controller',
            'finance_head',
            'tax_support',
            'billing_supervisor',
            'general_manager_marketing',
            'marketing_admin',
            'marketing_support',
            tempData !== 'need_approval' || current_approval !== userRole
              ? 'distributor_manager'
              : '',
          ]);
          const hasCancelAccess = !hasNoAccess(['superadmin', 'general']);
          const route = props.route;
          let title = 'Return Order Detail';
          if (route?.params?.titleScreen) {
            title = route.params.titleScreen;
          }
          const removeReturnOrder = () => {
            dispatch(
              showAlert({
                show: true,
                title: 'Delete Return Order',
                description:
                  'This Return order will be deleted. Do you want to proceed?',
                onCallback: async () => {
                  try {
                    if (route.params && route.params.id) {
                      dispatch(showPopup({ name: 'isLoading', value: true }));
                      const removeUser = await MsOrder.deleteReturnOrder(
                        route.params.id,
                      );
                      if (
                        removeUser &&
                        removeUser.data &&
                        removeUser.data.data
                      ) {
                        dispatch(
                          showPopup({ name: 'isLoading', value: false }),
                        );
                        Toast.show({
                          text1: 'Successfully deleted Return order',
                          type: 'success',
                        });
                        props.navigation.navigate('ReturnOrderScreen');
                      } else {
                        dispatch(
                          showPopup({ name: 'isLoading', value: false }),
                        );
                        Toast.show({
                          text1: 'Failed to delete Return order',
                          type: 'error',
                        });
                      }
                    }
                  } catch (error) {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'A system error occurred',
                      type: 'error',
                    });
                  }
                },
              }),
            );
          };
          const changeStatus = (value) => {
            const status = {
              title:
                value == 'cancelled'
                  ? 'Cancel Return Order'
                  : 'Set to Quotation',
              title_2:
                value == 'cancelled'
                  ? 'Return Order Cancelled'
                  : 'Return Order Submitted',
              description:
                value == 'cancelled'
                  ? 'This Return order will be cancelled. Do you want to proceed?'
                  : 'This Return order will be drafted. Do you want to proceed?',
              description_2:
                value == 'cancelled'
                  ? 'Return order has been cancelled'
                  : 'Return order has been drafted',
            };
            dispatch(
              showAlert({
                show: true,
                title: status.title,
                description: status.description,
                onCallback: async () => {
                  try {
                    if (route.params.id) {
                      dispatch(showPopup({ name: 'isLoading', value: true }));
                      const requestConfirm =
                        await MsOrder.updateReturnOrderStatus(
                          route.params.id,
                          value,
                        );
                      if (
                        requestConfirm &&
                        requestConfirm.data &&
                        requestConfirm.data.data
                      ) {
                        dispatch(
                          showPopup({ name: 'isLoading', value: false }),
                        );
                        dispatch(
                          showAlert({
                            show: true,
                            title: status.title_2,
                            description: status.description_2,
                            isConfirm: false,
                          }),
                        );
                        setRefresh(!refresh);
                        props.navigation.navigate('ReturnOrderDetailScreen', {
                          refresh: refresh,
                          ...route.params,
                        });
                      } else {
                        dispatch(
                          showPopup({ name: 'isLoading', value: false }),
                        );
                        Toast.show({
                          text1: 'A system error occurred',
                          type: 'error',
                        });
                      }
                    }
                  } catch (error) {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'A system error occurred',
                      type: 'error',
                    });
                  }
                },
              }),
            );
          };

          const menus = [];
          if (hasAccess) {
            if (
              !['need_approval', 'approved', 'cancelled'].includes(tempData)
            ) {
              menus.push({
                type: 'redirect',
                name: 'Edit',
                screen: 'ReturnOrderFormScreen',
                params: { ...route.params, firstAdded: '' },
              });
            }
            if (
              tempData == 'need_approval' ||
              (tempData == 'approved' && hasCancelAccess)
            ) {
              menus.push({
                type: 'function',
                name: 'Cancel',
                callback: () => changeStatus('cancelled'),
              });
            }
            if (tempData == 'draft') {
              menus.push({
                type: 'function',
                name: 'Delete',
                callback: () => removeReturnOrder(),
              });
            }
          }

          const goBackKey = props?.route?.params?.go_back_key || '';
          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton
                {...props}
                // isJump forces the BackButton's customBack to NAVIGATE (pop to the
                // existing screen) instead of navigation.replace(), which would stack
                // a new ReturnOrderScreen and remount the list (losing scroll + pages).
                isJump={true}
                notUseLastRoute={true}
                onPress={
                  props?.route?.params?.isFilter && isNative
                    ? () =>
                        props.navigation.navigate('ReturnOrderScreen', {
                          key: goBackKey,
                          isFilter: true,
                        })
                    : goBackKey
                    ? () => props.navigation.goBack(goBackKey)
                    : false
                }
                customBack={
                  props?.route?.params?.activityId
                    ? {
                        screen: 'ReturnActivityNavigator',
                        params: {
                          screen: 'ReturnActivityScreen',
                          params: {
                            id: props.route.params.activityId,
                            direct: 'orders',
                          },
                        },
                      }
                    : props?.route?.params?.isTask
                    ? {
                        screen: 'TaskNotificationNavigator',
                        params: {
                          screen: 'TaskNotificationScreen',
                          params: {
                            task: true,
                          },
                        },
                      }
                    : { screen: 'ReturnOrderScreen' }
                }
              />
            ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : (
                <View style={styles.headerRightRow}>
                  {route.params?.id ? (
                    <CommentHeaderButton
                      onPress={() =>
                        props.navigation.setParams({ showComments: true })
                      }
                      drawerVisible={Boolean(route.params?.showComments)}
                      entityType="return_order"
                      entityId={route.params.id}
                    />
                  ) : (
                    false
                  )}
                  {menus.length ? <MoreButton menu={menus} {...props} /> : null}
                </View>
              ),
          };
        }}
      />
      <Stack.Screen
        name="ReturnOrderInvoiceScreen"
        component={ReturnOrderInvoiceScreen}
        options={(props) => ({
          title: setTitleHeader('Invoice'),
          headerLeft: () => {
            const isCustomer = props?.route?.params?.customerId ? true : false;
            let params = {
              screen: 'ReturnOrderDetailScreen',
              params: {
                id: props.route.params?.ReturnOrderId || '',
                name: props.route.params?.ReturnOrderName || '',
                titleScreen: props.route.params?.ReturnOrderName || '',
                ...(props?.route?.params?.isTask ? { isTask: true } : {}),
              },
            };
            if (isCustomer) {
              params = {
                screen: 'CustomerNavigator',
                params: {
                  screen: 'CustomerDetailScreen',
                  params: {
                    id: props.route.params?.customerId || '',
                    name: props.route.params?.titleScreen || '',
                  },
                },
              };
            }
            return (
              <BackButton {...props} isJump={isCustomer} customBack={params} />
            );
          },
          headerRight: () =>
            isWeb ? <NotificationSettingButton {...props} /> : false,
        })}
      />

      <Stack.Screen
        name="ReturnOrderDocumentScreen"
        component={ReturnOrderDocumentScreen}
        options={(props) => ({
          title: setTitleHeader('Attachment'),
          headerLeft: () => (
            <BackButton
              {...props}
              customBack={{
                screen: 'ReturnOrderDetailScreen',
                params: {
                  id: props.route.params.ReturnOrderId,
                  name: props.route.params.ReturnOrderName,
                  titleScreen: props.route.params.ReturnOrderName,
                  ...(props?.route?.params?.isTask ? { isTask: true } : {}),
                },
              }}
            />
          ),
          headerRight: () =>
            isWeb ? <NotificationSettingButton {...props} /> : false,
        })}
      />
      <Stack.Screen
        name="ReturnOrderFormScreen"
        component={ReturnOrderFormScreen}
        options={(props) => {
          const route = props.route;
          let title = 'Add Return Order';
          if (route.params && route.params.titleScreen) {
            title = 'Edit Return Order';
          }
          return {
            title: setTitleHeader(title),
            headerRight: isWeb
              ? () => <NotificationSettingButton {...props} />
              : false,
          };
        }}
      />
    </Stack.Navigator>
  );
};

const styles = StyleSheet.create({
  headerRightRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default ReturnOrderNavigator;
